import { useQuery } from '@tanstack/react-query';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface VersionDetails {
  major: number;
  minor: number;
  patch: number;
  toString: () => string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

interface VersionResponse {
  version: string;
  details: VersionDetails;
  changelog: ChangelogEntry[];
}

export function VersionInfo() {
  const { data, isLoading, error } = useQuery<VersionResponse>({
    queryKey: ['/api/version'],
  });

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Loading...</div>;
  }

  if (error || !data) {
    return <div className="text-xs text-muted-foreground">v1.0.12</div>;
  }

  return (
    <TooltipProvider>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-auto py-0 px-0">
                <span className="text-xs text-muted-foreground">
                  {data.version}
                </span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Click to view changelog</p>
          </TooltipContent>
        </Tooltip>
        
        <PopoverContent className="w-80 p-0" align="center">
          <div className="p-4 pb-0">
            <h3 className="font-medium text-base">SaaS Management Platform</h3>
            <p className="text-sm text-muted-foreground">Version {data.version}</p>
          </div>
          <div className="p-4 pt-2">
            <h4 className="text-sm font-medium mb-2">Changelog</h4>
            <div className="max-h-60 overflow-auto">
              {data.changelog.map((entry) => (
                <div key={entry.version} className="mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{entry.version}</span>
                    <span className="text-xs text-muted-foreground">{entry.date}</span>
                  </div>
                  <ul className="mt-1 space-y-1">
                    {entry.changes.map((change, i) => (
                      <li key={i} className="text-xs ml-4 list-disc">{change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}